/**
 * canna canna - Unified Motion, Reveal, Stagger & Count-Up Engine
 * Supports all product LP variations (18 sections) seamlessly.
 */
(() => {
  if (window.__ccLpMotionEngineLoaded) return;
  window.__ccLpMotionEngineLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // All known reveal attributes across the codebase
  const REVEAL_SELECTORS = [
    '[data-cc-reveal]',
    '[data-cbx-reveal]',
    '[data-crdb-reveal]',
    '[data-cc-bliss-reveal]',
    '[data-cc-baby-reveal]',
    '[data-cc-dayhaze-reveal]',
    '[data-cc-jucycherry-reveal]',
    '[data-cc-juicybomb-reveal]',
    '[data-cc-lemonskunk-reveal]',
    '[data-cc-mojito-reveal]',
    '[data-cc-white-peach-reveal]',
    '[data-cc-dc650-reveal]',
    '[data-cc-herb-reveal]',
    '[data-cc-summer-reveal]'
  ].join(', ');

  // Value parser: handles numbers with prefixes, suffixes, commas, decimals
  const parseValue = (text) => {
    if (!text) return null;
    const clean = text.trim();
    // Check if string contains at least one digit
    if (!/\d/.test(clean)) return null;

    // Pattern matches optional prefix, signed/decimal number, optional suffix
    const match = clean.replace(/,/g, '').match(/^(.*?)(\d+(?:\.\d+)?)(.*)$/);
    if (!match) return null;

    const prefix = match[1];
    const num = parseFloat(match[2]);
    const suffix = match[3];
    const decParts = match[2].split('.');
    const decimals = decParts.length > 1 ? decParts[1].length : 0;

    if (!Number.isFinite(num) || num <= 0) return null;

    return { prefix, value: num, suffix, decimals, original: clean };
  };

  const formatValue = (data, val) => {
    const formattedNum = val.toLocaleString('ja-JP', {
      minimumFractionDigits: data.decimals,
      maximumFractionDigits: data.decimals
    });
    return `${data.prefix}${formattedNum}${data.suffix}`;
  };

  // Run count-up animation on a set of targets
  const animateCountUp = (targets, duration = 1400) => {
    if (!targets.length) return;
    if (reducedMotion.matches) {
      targets.forEach(({ el, data }) => { el.textContent = data.original; });
      return;
    }

    // Set initial 0
    targets.forEach(({ el, data }) => { el.textContent = formatValue(data, 0); });

    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      targets.forEach(({ el, data }) => {
        el.textContent = formatValue(data, data.value * eased);
      });

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        targets.forEach(({ el, data }) => { el.textContent = data.original; });
      }
    };
    requestAnimationFrame(tick);
  };

  // Initialize a Facts / Specs block
  const initFactsBlock = (block) => {
    if (block.dataset.ccCountReady === 'true') return;
    block.dataset.ccCountReady = 'true';

    // Collect candidate elements: strong, dd
    const candidates = Array.from(block.querySelectorAll('strong, dd, [data-cc-countup]'));
    const validTargets = candidates
      .filter((el) => !el.closest('figcaption') && !el.closest('dt'))
      .map((el) => {
        // Read dataset value first if available, otherwise textContent
        const raw = el.dataset.value || el.textContent || '';
        const data = parseValue(raw);
        return data ? { el, data } : null;
      })
      .filter(Boolean);

    if (!validTargets.length) return;

    if ('IntersectionObserver' in window && !reducedMotion.matches) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        animateCountUp(validTargets, 1500);
      }, { rootMargin: '0px 0px -15% 0px', threshold: 0.1 });
      observer.observe(block);
    } else {
      validTargets.forEach(({ el, data }) => { el.textContent = data.original; });
    }
  };

  // Initialize Recipe bar animation & countup
  const initRecipeBlock = (recipeEl) => {
    if (recipeEl.dataset.ccRecipeReady === 'true') return;
    recipeEl.dataset.ccRecipeReady = 'true';

    const rows = Array.from(recipeEl.querySelectorAll('.cc-composition-row, dl > div'));
    rows.forEach((row, idx) => {
      row.style.setProperty('--cc-row-index', idx);
    });

    const triggerBars = () => {
      recipeEl.classList.add('is-cc-bars-ready');
      // Also animate values in recipe if present
      const valElements = Array.from(recipeEl.querySelectorAll('.cc-composition-row__val, dd > strong'));
      const targets = valElements.map((el) => {
        const raw = el.dataset.ccRecipeVal || el.textContent || '';
        const data = parseValue(raw);
        return data ? { el, data } : null;
      }).filter(Boolean);
      if (targets.length) {
        animateCountUp(targets, 1200);
      }
    };

    if ('IntersectionObserver' in window && !reducedMotion.matches) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        triggerBars();
      }, { rootMargin: '0px 0px -15% 0px', threshold: 0.1 });
      observer.observe(recipeEl);
    } else {
      triggerBars();
    }
  };

  // Stagger index helper for lists / cards
  const applyStaggerIndexes = (container, itemSelector) => {
    const items = container.querySelectorAll(itemSelector);
    items.forEach((item, i) => {
      item.style.setProperty('--cc-index', i);
    });
  };

  // Main init routine
  const initAll = (root = document) => {
    // 1. Reveal items
    const revealItems = root.querySelectorAll(REVEAL_SELECTORS);
    if (revealItems.length) {
      if ('IntersectionObserver' in window && !reducedMotion.matches) {
        const revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const target = entry.target;
            target.setAttribute('data-visible', '');
            target.setAttribute('data-cc-visible', '');
            target.setAttribute('data-cc-herb-visible', '');
            target.setAttribute('data-cc-summer-visible', '');
            target.classList.add('is-cc-visible');
            revealObserver.unobserve(target);
          });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

        revealItems.forEach((el) => {
          // Set motion ready on parent section if applicable
          const parentSec = el.closest('section, div[id*="cc-"]');
          if (parentSec) {
            parentSec.dataset.motion = 'ready';
            parentSec.dataset.ccMotion = 'ready';
            parentSec.dataset.ccHerbMotion = 'ready';
            parentSec.dataset.ccSummerMotion = 'ready';
          }
          revealObserver.observe(el);
        });
      } else {
        revealItems.forEach((el) => {
          el.setAttribute('data-visible', '');
          el.setAttribute('data-cc-visible', '');
          el.setAttribute('data-cc-herb-visible', '');
          el.setAttribute('data-cc-summer-visible', '');
          el.classList.add('is-cc-visible');
        });
      }
    }

    // 2. Stagger indexes for child elements
    root.querySelectorAll('[class*="__facts"], .cc-lp-specs, .cc-dc650__facts, .cc-herb__facts').forEach((facts) => {
      applyStaggerIndexes(facts, ':scope > div, :scope dl > div');
      initFactsBlock(facts);
    });

    root.querySelectorAll('.cc-lp-recipe, .cc-baby__recipe, .cc-dayhaze__recipe, .cc-jucycherry__recipe, .cc-juicybomb__recipe, .cc-lemonskunk__recipe, .cc-mojito__recipe, .cc-herb__formula, .cc-dc650__specs').forEach((recipe) => {
      initRecipeBlock(recipe);
    });

    root.querySelectorAll('.cc-lp-moments, .cc-baby__choose, .cc-dayhaze__choose, .cc-jucycherry__moments, .cc-juicybomb__choose, .cc-lemonskunk__moments, .cc-mojito__moments, .cc-white-peach__choose, .cc-dc650__howto').forEach((moments) => {
      applyStaggerIndexes(moments, 'li, article, div.cc-session__mode');
    });

    root.querySelectorAll('.cc-session__modes, .cc-session__blend, .cc-summer__cards').forEach((group) => {
      applyStaggerIndexes(group, 'article, div');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
